import axios, { CancelTokenSource } from 'axios'
import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import useErrorHandler from '../../hooks/useErrorHandler'
import ChoiceService from '../../services/ChoiceService'
import FormErrorDisplay from '../common/FormErrorMessages'
import GetHelpDecisionStep from './GetHelpDecisionStep'
import {
  IChoices,
  ISelectedChoices,
  ISelectedChoice,
} from '../../models/Choice'
import Conditional from '../common/Conditional'
import FormDependencyLoaderContext from '../context/FormDependencyLoaderContext'

const GetHelpDecisionWizard = () => {
  const mountedRef = useRef(false)
  const loader = useContext(FormDependencyLoaderContext)
  const [formLoading, setFormLoading] = useState(false)
  const [loading, setLoading] = useState(true)
  const [getHelp, setGetHelp] = useState<IChoices>()
  const [userErrors, , handleErrors, , clearErrors] = useErrorHandler()
  const [step, setStep] = useState(0)
  const [selectedChoices, setSelectedChoices] = useState<ISelectedChoices>(null)

  const increase = useCallback(() => {
    setStep((step) => step + 1)
  }, [])

  const decrease = useCallback(() => {
    setStep((step) => step - 1)
  }, [])

  const getEntry = async (source: CancelTokenSource) => {
    return await ChoiceService.getEntry(source).then((response) => {
      return response.result
    })
  }

  const getNextSelectionByChoice = async (
    id: number,
    source: CancelTokenSource
  ) => {
    return await ChoiceService.getNextSelectionByChoice(id, source).then(
      (response) => {
        return response.result
      }
    )
  }

  const getPreviousSelectionByChoice = async (
    id: number,
    source: CancelTokenSource
  ) => {
    return await ChoiceService.getPreviousSelectionByChoice(id, source).then(
      (response) => {
        return response.result
      }
    )
  }

  useEffect(() => {
    mountedRef.current = true
    const source = axios.CancelToken.source()
    if (mountedRef.current) {
      loader.show()
      getEntry(source)
        .then((result) => {
          setSelectedChoices((prevState) => ({
            ...prevState,
            selections: [],
          }))

          setGetHelp((prevState) => ({
            ...prevState,
            id: result.id,
            contentHeader: result.contentHeader,
            contentMain: result.contentMain,
            contentGuidance: result.contentGuidance,
            choices: result.choiceOptions,
            selectedChoice:
              result.choiceOptions.length > 0
                ? result.choiceOptions[0].id
                : null,
          }))
        })
        .catch((error) => {
          handleErrors(error)
        })
        .finally(() => {
          setLoading(false)
          loader.hide()
        })
    }
    return () => {
      mountedRef.current = false
    }
  }, [])

  const forward = (value: ISelectedChoice) => {
    clearErrors()
    const source = axios.CancelToken.source()
    if (value === null) {
      return
    }
    const result = selectedChoices.selections.find((k) => k.step === value.step)
    if (result === null || result === undefined) {
      setSelectedChoices((prevState) => {
        return {
          ...prevState,
          selections: prevState.selections.concat(value),
        }
      })
    } else {
      setSelectedChoices((prevState) => {
        return {
          ...prevState,
          selections: prevState.selections
            .filter((p) => p.step !== value.step)
            .concat(value),
        }
      })
    }

    setFormLoading(true)
    loader.show()
    getNextSelectionByChoice(value.id, source)
      .then((result) => {
        setGetHelp((prevState) => ({
          ...prevState,
          id: value.id,
          contentHeader: result.contentHeader,
          contentMain: result.contentMain,
          choiceContent: result.choiceContent,
          contentGuidance: result.contentGuidance,
          choices: result.choiceOptions,
          isLinkedContent: result.IsLinkedContent,
          
        }))
        increase()
      })
      .catch((error) => {
        handleErrors(error)
      })
      .finally(() => {
        loader.hide()
        setFormLoading(false)
      })
  }

  const backward = (fromStep: number) => {
    clearErrors()
    const source = axios.CancelToken.source()
    loader.show()
    const choiceSelection = selectedChoices.selections.find(
      (k) => k.step === fromStep - 1
    )
    setFormLoading(true)
    getPreviousSelectionByChoice(choiceSelection.id, source)
      .then((result) => {
        setGetHelp((prevState) => ({
          ...prevState,
          id: result.id,
          selectedChoice: choiceSelection?.id,
          contentHeader: result.contentHeader,
          contentMain: result.contentMain,
          choiceContent: result.choiceContent,
          contentGuidance: result.contentGuidance,
          choices: result.choiceOptions,
          isLinkedContent: result.IsLinkedContent
        }))
        decrease()
      })
      .catch((error) => {
        handleErrors(error)
      })
      .finally(() => {
        loader.hide()
        setFormLoading(false)
      })
  }

  return (
    <>
      <FormErrorDisplay
        show={userErrors?.length > 0}
        errorsList={userErrors}
        heading="Please review the following errors"
      />

      <Conditional if={!loading}>
        <GetHelpDecisionStep
          loading={formLoading}
          step={step}
          value={getHelp?.selectedChoice}
          header={getHelp?.contentHeader}
          contentMain={getHelp?.contentMain}
          contentGuidance={getHelp?.contentGuidance}
          choices={getHelp?.choices}
          forward={forward}
          backward={backward}
        />
      </Conditional>
    </>
  )
}

export default GetHelpDecisionWizard
